import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Play,
  Plus,
  Clock,
  Users,
  Target
} from 'lucide-react';
import { PlayerTile } from '@/components/coach/PlayerTile';
import { ShotStream } from '@/components/coach/ShotStream';
import { SessionMetrics } from '@/components/coach/SessionMetrics';
import { SessionControls } from '@/components/coach/SessionControls';
import { AlertsPanel } from '@/components/coach/AlertsPanel';
import { EnvironmentData } from '@/components/coach/EnvironmentData';
import { CoachingNotes } from '@/components/coach/CoachingNotes';
import { AICoachingPanel } from '@/components/coach/AICoachingPanel';
import { useSocket } from '@/hooks/useSocket';
import { shotAPI } from '@/services/api';
import { toast } from '@/hooks/use-toast';

interface Player {
  id: string;
  name: string;
  avatar?: string;
  status: 'active' | 'break' | 'needs_attention';
  currentMetrics: {
    shotsToday: number;
    averageDistance: number;
    consistencyScore: number;
    lastShotQuality: 'excellent' | 'good' | 'needs_work';
    improvementTrend: 'up' | 'stable' | 'down';
  };
  alerts: string[];
}

interface Shot {
  id: string;
  playerId: string;
  playerName: string;
  distance: number;
  quality: 'excellent' | 'good' | 'needs_work';
  shotType: string;
  timestamp: Date;
  notes?: string;
}

interface SessionData {
  id: string;
  name: string;
  startTime: Date;
  status: 'active' | 'paused' | 'ended';
  currentDrill: string;
  participants: Player[];
  overallMetrics: {
    totalShots: number;
    averageDistance: number;
    sessionDuration: number;
    activeParticipants: number;
  };
}

interface Alert {
  id: string;
  type: 'performance' | 'technique' | 'fatigue' | 'equipment';
  severity: 'low' | 'medium' | 'high';
  playerId: string;
  playerName: string;
  message: string;
  timestamp: Date;
}

interface EnvironmentMetrics {
  temperature: number;
  humidity: number;
  windSpeed: number;
  windDirection: string;
  conditions: string;
}

export function LiveSessionMonitor() {
  useAuth(); // For authentication context
  const { socket } = useSocket();

  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [shots, setShots] = useState<Shot[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [_environmentData] = useState<EnvironmentMetrics>({
    temperature: 22,
    humidity: 65,
    windSpeed: 8,
    windDirection: 'NW',
    conditions: 'Partly Cloudy'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [notes, setNotes] = useState('');

  // Mock data for development - replace with real API calls
  const mockSessionData: SessionData = {
    id: 'session-1',
    name: 'Morning Batting Practice',
    startTime: new Date(Date.now() - 45 * 60 * 1000), // 45 minutes ago
    status: 'active',
    currentDrill: 'Drive Practice',
    participants: [
      {
        id: 'player-1',
        name: 'Alex Johnson',
        status: 'active',
        currentMetrics: {
          shotsToday: 15,
          averageDistance: 78,
          consistencyScore: 85,
          lastShotQuality: 'excellent',
          improvementTrend: 'up'
        },
        alerts: []
      },
      {
        id: 'player-2',
        name: 'Sarah Mitchell',
        status: 'active',
        currentMetrics: {
          shotsToday: 12,
          averageDistance: 65,
          consistencyScore: 72,
          lastShotQuality: 'good',
          improvementTrend: 'stable'
        },
        alerts: []
      },
      {
        id: 'player-3',
        name: 'James Chen',
        status: 'needs_attention',
        currentMetrics: {
          shotsToday: 8,
          averageDistance: 45,
          consistencyScore: 55,
          lastShotQuality: 'needs_work',
          improvementTrend: 'down'
        },
        alerts: ['technique_issue', 'fatigue_detected']
      }
    ],
    overallMetrics: {
      totalShots: 35,
      averageDistance: 63,
      sessionDuration: 45,
      activeParticipants: 3
    }
  };

  const mockShots: Shot[] = [
    {
      id: 'shot-1',
      playerId: 'player-1',
      playerName: 'Alex Johnson',
      distance: 82,
      quality: 'excellent',
      shotType: 'Drive',
      timestamp: new Date(Date.now() - 30000),
      notes: 'Perfect timing and placement'
    },
    {
      id: 'shot-2',
      playerId: 'player-3',
      playerName: 'James Chen',
      distance: 38,
      quality: 'needs_work',
      shotType: 'Cut',
      timestamp: new Date(Date.now() - 120000),
      notes: 'Needs technique work'
    },
    {
      id: 'shot-3',
      playerId: 'player-2',
      playerName: 'Sarah Mitchell',
      distance: 71,
      quality: 'good',
      shotType: 'Pull',
      timestamp: new Date(Date.now() - 180000),
      notes: 'Good placement'
    }
  ];

  const mockAlerts: Alert[] = [
    {
      id: 'alert-1',
      type: 'fatigue',
      severity: 'medium',
      playerId: 'player-3',
      playerName: 'James Chen',
      message: 'Player showing signs of fatigue - consider break',
      timestamp: new Date(Date.now() - 300000)
    },
    {
      id: 'alert-2',
      type: 'technique',
      severity: 'low',
      playerId: 'player-3',
      playerName: 'James Chen',
      message: 'Consistent technique issues with cut shots',
      timestamp: new Date(Date.now() - 600000)
    }
  ];

  // Initialize with mock data
  useEffect(() => {
    const timer = setTimeout(() => {
      setSessionData(mockSessionData);
      setShots(mockShots);
      setAlerts(mockAlerts);
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Socket event handlers
  useEffect(() => {
    if (!socket || !sessionData) return;

    const handleShotUpdate = (shot: Shot) => {
      setShots(prev => [shot, ...prev.slice(0, 19)]); // Keep last 20 shots
      toast({
        title: 'New Shot Recorded',
        description: `${shot.playerName}: ${shot.distance}m ${shot.shotType}`,
      });
    };

    const handlePlayerUpdate = (playerUpdate: { playerId: string; metrics: any }) => {
      setSessionData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          participants: prev.participants.map(p => 
            p.id === playerUpdate.playerId 
              ? { ...p, currentMetrics: { ...p.currentMetrics, ...playerUpdate.metrics } }
              : p
          )
        };
      });
    };

    const handleAlert = (alert: Alert) => {
      setAlerts(prev => [alert, ...prev.slice(0, 9)]); // Keep last 10 alerts
      toast({
        title: `${alert.severity.toUpperCase()} Alert`,
        description: `${alert.playerName}: ${alert.message}`,
        variant: alert.severity === 'high' ? 'destructive' : 'default',
      });
    };

    socket.on('shot_recorded', handleShotUpdate);
    socket.on('player_metrics_updated', handlePlayerUpdate);
    socket.on('performance_alert', handleAlert);

    return () => {
      socket.off('shot_recorded', handleShotUpdate);
      socket.off('player_metrics_updated', handlePlayerUpdate);
      socket.off('performance_alert', handleAlert);
    };
  }, [socket, sessionData]);

  const handleSessionControl = async (action: 'pause' | 'resume' | 'end') => {
    if (!sessionData) return;

    try {
      switch (action) {
        case 'pause':
          setSessionData(prev => prev ? { ...prev, status: 'paused' } : prev);
          socket?.emit('session_paused', { sessionId: sessionData.id });
          toast({ title: 'Session Paused', description: 'The training session has been paused.' });
          break;
        case 'resume':
          setSessionData(prev => prev ? { ...prev, status: 'active' } : prev);
          socket?.emit('session_resumed', { sessionId: sessionData.id });
          toast({ title: 'Session Resumed', description: 'The training session has been resumed.' });
          break;
        case 'end':
          await shotAPI.endSession(sessionData.id);
          setSessionData(prev => prev ? { ...prev, status: 'ended' } : prev);
          toast({ title: 'Session Ended', description: 'The training session has been ended.' });
          break;
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `Failed to ${action} session. Please try again.`,
        variant: 'destructive',
      });
    }
  };


  const handleRemovePlayer = (playerId: string) => {
    if (!sessionData) return;
    
    setSessionData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        participants: prev.participants.filter(p => p.id !== playerId)
      };
    });
    
    toast({
      title: 'Player Removed',
      description: 'Player has been removed from the session.',
    });
  };

  const handleDrillChange = (drillName: string) => {
    if (!sessionData) return;
    
    setSessionData(prev => prev ? { ...prev, currentDrill: drillName } : prev);
    socket?.emit('drill_changed', { sessionId: sessionData.id, drill: drillName });
    
    toast({
      title: 'Drill Changed',
      description: `Now practicing: ${drillName}`,
    });
  };

  const handleAddNote = (note: string) => {
    setNotes(prev => prev + (prev ? '\n' : '') + `${new Date().toLocaleTimeString()}: ${note}`);
    
    toast({
      title: 'Note Added',
      description: 'Coaching note has been saved.',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading session data...</p>
        </div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Target className="mx-auto h-12 w-12 text-muted-foreground" />
          <h1 className="mt-4 text-xl font-semibold">No Active Session</h1>
          <p className="mt-2 text-muted-foreground">
            Start a new training session to begin monitoring.
          </p>
          <Button className="mt-4">
            <Play className="mr-2 h-4 w-4" />
            Start New Session
          </Button>
        </div>
      </div>
    );
  }

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Session Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{sessionData.name}</h1>
            <div className="flex items-center space-x-4 mt-2">
              <Badge 
                variant={sessionData.status === 'active' ? 'default' : 'secondary'}
                className="text-sm"
              >
                {sessionData.status === 'active' ? (
                  <>
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2" />
                    LIVE
                  </>
                ) : (
                  sessionData.status.toUpperCase()
                )}
              </Badge>
              <span className="text-muted-foreground flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {formatDuration(sessionData.overallMetrics.sessionDuration)}
              </span>
              <span className="text-muted-foreground">
                Current Drill: <span className="font-medium">{sessionData.currentDrill}</span>
              </span>
            </div>
          </div>
          
          <SessionControls
            sessionStatus={sessionData.status}
            onPause={() => handleSessionControl('pause')}
            onResume={() => handleSessionControl('resume')}
            onEnd={() => handleSessionControl('end')}
            onDrillChange={handleDrillChange}
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Session Metrics */}
          <div className="lg:col-span-1">
            <SessionMetrics metrics={sessionData.overallMetrics} />
          </div>

          {/* Environment Data */}
          <div className="lg:col-span-1">
            <EnvironmentData data={_environmentData} />
          </div>

          {/* Alerts Panel */}
          <div className="lg:col-span-2">
            <AlertsPanel alerts={alerts} onDismissAlert={(alertId) => {
              setAlerts(prev => prev.filter(a => a.id !== alertId));
            }} />
          </div>
        </div>

        {/* Player Tiles */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Active Players ({sessionData.participants.length})
            </h2>
            <Button variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Player
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sessionData.participants.map(player => (
              <PlayerTile
                key={player.id}
                player={player}
                onRemove={() => handleRemovePlayer(player.id)}
              />
            ))}
          </div>
        </div>

        {/* Bottom Section - Shot Stream, AI Coaching, and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Shot Stream */}
          <div className="lg:col-span-2">
            <ShotStream shots={shots} />
          </div>

          {/* AI Coaching Panel */}
          <div className="lg:col-span-1">
            <AICoachingPanel
              sessionId={sessionData.id}
              playerId={sessionData.participants[0]?.id || ''}
              playerName={sessionData.participants[0]?.name || 'Player'}
              latestShot={shots[0]}
              recentAnalyses={[]}
              onInterventionRequest={(urgency) => {
                console.log('Intervention requested:', urgency);
              }}
            />
          </div>

          {/* Coaching Notes */}
          <div className="lg:col-span-1">
            <CoachingNotes
              notes={notes}
              onAddNote={handleAddNote}
            />
          </div>
        </div>
      </div>
    </div>
  );
}